import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AlertTriangle, Boxes, BrainCircuit, Search, LayoutList, ArrowRight, ArrowLeft, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageReader from "@/components/PageReader";
import { Reveal } from "@/lib/motion";
import { SOLUTIONS, SOLUTIONS_ORDER } from "@/data/solutions";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";

export default function Solutions() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const sol = slug ? SOLUTIONS[slug] : null;
  const seoKey = slug ? `/solutions/${slug}` : "/solutions";
  usePageSeo(
    PAGE_SEO[seoKey] ||
      (sol
        ? {
            title: `${sol.name} Solutions by WebsiteTech.pro`,
            description: (sol.intro || sol.summary || `${sol.name} AI, SEO and growth solutions by WebsiteTech.pro — built for how your industry actually works.`).slice(0, 170),
          }
        : {
            title: "Industry Solutions | WebsiteTech.pro",
            description: "WebsiteTech industry solutions — restaurants, healthcare, hotels, professional services, ecommerce and more. Built for how each industry actually works.",
          })
  );
  useBreadcrumbSchema(
    slug
      ? [{ name: "Home", url: "/" }, { name: "Industries", url: "/solutions" }, { name: sol?.name || slug, url: seoKey }]
      : [{ name: "Home", url: "/" }, { name: "Industries", url: "/solutions" }]
  );
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!slug) return <Index />;
  if (!sol) return <Index notFound />;

  const goAnalyze = () => { navigate("/"); setTimeout(() => document.getElementById("analyzer")?.scrollIntoView({ behavior: "smooth" }), 400); };

  const idx = SOLUTIONS_ORDER.indexOf(slug);
  const prevSlug = idx > 0 ? SOLUTIONS_ORDER[idx - 1] : null;
  const nextSlug = idx >= 0 && idx < SOLUTIONS_ORDER.length - 1 ? SOLUTIONS_ORDER[idx + 1] : null;

  return (
    <>
      <Navbar />
      <main className="pt-24">
        <section className="relative h-[46vh] min-h-[360px] overflow-hidden flex items-end">
          <img src={sol.image} alt={sol.name} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B203E]/90 via-slate-900/50 to-slate-900/30" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full">
            <Link to="/solutions" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-4"><ArrowLeft size={14} /> All solutions</Link>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-300">Industry Solution</p>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white tracking-tight mt-2">{sol.name}</h1>
            <p className="text-white/80 text-lg mt-3 max-w-2xl">{sol.tagline}</p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Block icon={AlertTriangle} title="Challenges We Solve" items={sol.challenges} tone="#DC2626" />
          <Block icon={Boxes} title="Recommended Features" items={sol.features} tone="#2563EB" />
          <Block icon={BrainCircuit} title="AI Opportunities" items={sol.ai} tone="#7C3AED" />
          <Block icon={Search} title="SEO Considerations" items={sol.seo} tone="#16A34A" />
          <div className="lg:col-span-2 bg-slate-50 border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-4"><LayoutList size={16} className="text-brand" /><span className="overline">Sample Website Structure</span></div>
            <div className="flex flex-wrap gap-2">
              {sol.structure.map((p) => <span key={p} className="text-sm font-medium bg-white border border-slate-200 rounded-lg px-4 py-2 text-slate-700">{p}</span>)}
            </div>
          </div>
        </div>

        {sol.sections && (
          <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-page-reader-root>
            <PageReader title={`${sol.name} Website Solutions`} />
            {sol.journey && (
              <div className="flex flex-wrap items-center gap-2 mb-8">
                {sol.journey.split("\u2192").map((step, k) => (
                  <span key={k} className="text-sm font-medium bg-blue-50 text-brand rounded-lg px-3 py-1.5">{step.trim()}</span>
                ))}
              </div>
            )}
            {sol.intro?.map((p, i) => <p key={i} className="text-lg text-slate-600 leading-relaxed mb-4">{p}</p>)}
            {sol.sections.map((s, i) => (
              <Reveal key={i} className="mt-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-4">{s.heading}</h2>
                {s.paras?.map((p, j) => <p key={j} className="text-slate-600 text-lg leading-relaxed mb-4">{p}</p>)}
                {s.flow && (
                  <div className="flex flex-wrap items-center gap-2 my-5">
                    {s.flow.split("\u2192").map((step, k) => <span key={k} className="text-sm font-medium bg-blue-50 text-brand rounded-lg px-3 py-1.5">{step.trim()}</span>)}
                  </div>
                )}
                {s.list && (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4">
                    {s.list.map((it, j) => <li key={j} className="flex gap-2.5 text-slate-600"><Check size={16} className="text-green-600 mt-1 shrink-0" /> <span>{it}</span></li>)}
                  </ul>
                )}
              </Reveal>
            ))}
          </article>
        )}

        {(prevSlug || nextSlug) && (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 flex flex-col sm:flex-row gap-3">
            {prevSlug ? (
              <Link to={`/solutions/${prevSlug}`} data-testid="solution-prev" className="flex-1 group border border-slate-200 rounded-xl p-4 hover:border-brand transition-colors">
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400"><ArrowLeft size={13} /> Previous Industry</span>
                <span className="block text-sm font-semibold text-slate-800 mt-1 group-hover:text-brand transition-colors">{SOLUTIONS[prevSlug].name}</span>
              </Link>
            ) : <div className="flex-1 hidden sm:block" />}
            {nextSlug ? (
              <Link to={`/solutions/${nextSlug}`} data-testid="solution-next" className="flex-1 group bg-[#0B203E] rounded-xl p-4 hover:bg-brand transition-colors text-right">
                <span className="flex items-center justify-end gap-1.5 text-[11px] font-bold uppercase tracking-wide text-white/50">Next Industry <ArrowRight size={13} /></span>
                <span className="block text-sm font-semibold text-white mt-1">{SOLUTIONS[nextSlug].name}</span>
              </Link>
            ) : <div className="flex-1 hidden sm:block" />}
          </div>
        )}

        <section className="bg-brand py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">Ready to grow your {sol.name.toLowerCase()} business?</h2>
            <button onClick={goAnalyze} className="bg-white text-brand font-semibold rounded-xl px-8 py-4 mt-6 inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
              Analyze Your Website <ArrowRight size={16} />
            </button>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function Block({ icon: Icon, title, items, tone }) {
  return (
    <Reveal>
      <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 h-full">
        <div className="flex items-center gap-2 mb-4"><Icon size={17} style={{ color: tone }} /><h3 className="font-heading font-bold text-lg text-slate-900">{title}</h3></div>
        <ul className="space-y-2.5">
          {items.map((it) => <li key={it} className="flex gap-2.5 text-sm text-slate-600"><span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tone }} /> {it}</li>)}
        </ul>
      </div>
    </Reveal>
  );
}

function Index({ notFound }) {
  return (
    <>
      <Navbar />
      <main className="pt-32 pb-24 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="overline">Industry Solutions</span>
          <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight mt-3">Websites built for your industry</h1>
          {notFound && <p className="text-red-500 mt-3">That solution wasn't found — here are all of them.</p>}

          {/* Featured module — AI Commerce Intelligence */}
          <Link to="/solutions/ai-commerce-intelligence"
            data-testid="solutions-featured-commerce"
            className="mt-10 block relative rounded-2xl overflow-hidden border border-emerald-400/40 bg-gradient-to-br from-emerald-500/10 via-slate-900/5 to-indigo-500/10 p-8 hover:border-emerald-400 hover:shadow-xl transition group">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6">
              <div className="flex-1">
                <div className="inline-flex text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-600 border border-emerald-400/40 bg-emerald-50 rounded-full px-2 py-0.5">Ecommerce & Retail · NEW</div>
                <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-slate-900">AI Commerce Intelligence</h2>
                <p className="mt-2 text-slate-600 max-w-2xl">Turn your store, sales, inventory and competitor data into profitable pricing, promotion and inventory decisions — approval-required, guardrail-safe, explainable.</p>
                <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-semibold">
                  {["Profit Center","Competitor Monitor","Smart Pricing","Promotion Optimizer","Inventory & Demand","Action Center"].map((t) => (
                    <span key={t} className="bg-white/70 border border-emerald-300/40 text-emerald-800 rounded-full px-3 py-1">{t}</span>
                  ))}
                </div>
              </div>
              <div className="inline-flex items-center gap-2 bg-emerald-600 group-hover:bg-emerald-500 text-white rounded-lg px-5 py-3 font-bold text-sm transition">
                Explore module <ArrowRight size={16} />
              </div>
            </div>
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-12">
            {Object.entries(SOLUTIONS).map(([slug, s]) => (
              <Link key={slug} to={`/solutions/${slug}`} className="relative h-64 rounded-2xl overflow-hidden border border-slate-200 group soft-shadow">
                <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B203E]/85 via-slate-900/40 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-7">
                  <h3 className="font-heading text-2xl font-bold text-white">{s.name}</h3>
                  <p className="text-sm text-white/70 mt-1">{s.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
